import * as React from 'react';

import useLabelTranslation from 'src/hooks/useLabelTranslation';

import type {ApiSchedulerSyncedAgent} from 'src/types/scheduler';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu/Menu';

import classify from 'src/utils/classify';
import CalendarConnectionButton from './calendar-connection-button';
import SenseLogo from 'src/images/sense-new-logo.svg';
import TeamsLogo from 'src/images/scheduler/microsoft-teams.svg';
import ZoomLogo from 'src/images/scheduler/zoomus-icon.svg';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {BasicSingleCell} from '@spaced-out/ui-design-system/lib/components/Table';
import {BodyMedium} from '@spaced-out/ui-design-system/lib/components/Text';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {
  Toast,
  ToastBody,
  ToastFooter,
  ToastTitle,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {TruncatedTextWithTooltip} from '@spaced-out/ui-design-system/lib/components/TruncatedTextWithTooltip';
import {
  googleProvidersKeys,
  microsoftProvidersKeys,
  GOOGLE_PROVIDER,
  MICROSOFT_PROVIDER,
  VIRTUAL_CALENDAR_PROVIDER,
} from './accounts/utils/constant';
import {displayTime} from 'src/utils/date-time-2';

import css from './calendar-sync.css';


type TranslationFunction = (key: string, value: string) => string;

export const providerMap: {
  [key: string]: string;
} = {
  [MICROSOFT_PROVIDER]: 'Microsoft',
  [GOOGLE_PROVIDER]: 'Google',
  [VIRTUAL_CALENDAR_PROVIDER]: 'Sense',
};

export const getConnectedStateFilterOptions = ({
  t,
  serviceAccountProvider,
  isServiceAccountActive,
  isVirtualCalendarEnabled,
}: {
  t: TranslationFunction;
  serviceAccountProvider?: string;
  isServiceAccountActive: boolean | null | undefined;
  isVirtualCalendarEnabled: boolean | null | undefined;
}): MenuOption[] => {
  const providerConfig = {
    [GOOGLE_PROVIDER]: t(
      'Connected_to_Google_Calendar',
      'Connected to Google Calendar',
    ),
    [MICROSOFT_PROVIDER]: t(
      'Connected_to_Microsoft_Calendar',
      'Connected to Microsoft Calendar',
    ),
  } as const;

  const provider = serviceAccountProvider
    ? getNormalizedServiceAccountProvider(serviceAccountProvider)
    : null;

  const options: Array<
    | {
        key: string;
        label: any;
      }
    | {
        key: string;
        label: string;
      }
  > = [];

  if (isVirtualCalendarEnabled && provider) {
    options.push({
      key: VIRTUAL_CALENDAR_PROVIDER,
      label: t(
        'Connected_to_Sense_Calendar',
        `Connected${isServiceAccountActive ? ' to Sense Calendar' : ''}`,
      ),
    });

    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly google: string; readonly microsoft: string; }'.
    if (providerConfig[provider]) {
      options.push({
        key: provider,
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly google: string; readonly microsoft: string; }'.
        label: providerConfig[provider],
      });
    }
  }

  if (!isVirtualCalendarEnabled || !provider) {
    options.push({
      key: 'connected',
      label: t('Connected', 'Connected'),
    });
  }

  return [
    ...options,
    {
      key: 'disconnected',
      label: t('Not_Connected', 'Not Connected'),
    },
    {
      key: 'all',
      label: t('All_Accounts', 'All Accounts'),
    },
  ];
};

const getCalendarConfig = (t: TranslationFunction) => {
  return {
    [VIRTUAL_CALENDAR_PROVIDER]: {
      icon: <SenseLogo className={css.connectCalenderIcon} />,
      label: t('Sense_Calendar', 'Sense Calendar'),
    },
    [GOOGLE_PROVIDER]: {
      icon: <Icon color="primary" name="google" size="small" type="brands" />,
      label: t('Google_Calendar', 'Google Calendar'),
    },
    [MICROSOFT_PROVIDER]: {
      icon: (
        <Icon color="primary" name="microsoft" size="small" type="brands" />
      ),
      label: t('Microsoft_Calendar', 'Microsoft Calendar'),
    },
  };
};

const createCalendarOption = (
  t: TranslationFunction,
  provider: string,
  showCount?: boolean,
  connectedIdsByProvider?: {
    [provider: string]: string[];
  },
): MenuOption | null | undefined => {
  const calendarConfig = getCalendarConfig(t);
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ "virtual-calendar": { icon: Element; label: string; }; google: { icon: Element; label: string; }; microsoft: { icon: Element; label: string; }; }'.
  const config = calendarConfig[provider];

  if (!config) {
    return null;
  }

  let countDisplay = '';
  let count = 0;

  if (showCount && connectedIdsByProvider) {
    const googleCount = (connectedIdsByProvider.google || []).length;
    const microsoftCount = (connectedIdsByProvider.microsoft || []).length;
    const virtualCalendarCount = (
      connectedIdsByProvider[VIRTUAL_CALENDAR_PROVIDER] || []
    ).length;

    if (provider === VIRTUAL_CALENDAR_PROVIDER) {
      // For Sense Calendar option, show count of Google + Microsoft users
      count = googleCount + microsoftCount;
    } else if (
      provider === GOOGLE_PROVIDER ||
      provider === MICROSOFT_PROVIDER
    ) {
      // For Google/Microsoft options, show count of Sense Calendar users
      count = virtualCalendarCount;
    }
    countDisplay = count > 0 ? ` (${count})` : ' (0)';
  }

  return {
    key: provider,
    customComponent: (
      <div className={css.calendarContainerOption}>
        {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
        {config.icon}
        {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
        {config.label}
        {countDisplay}
      </div>
    ),
  };
};

export const getConnectCalendarOptions = ({
  t,
  showCount,
  serviceAccountProvider,
  connectedIdsByProvider,
}: {
  t: TranslationFunction;
  showCount?: boolean;
  serviceAccountProvider?: string;
  connectedIdsByProvider?: {
    [provider: string]: string[];
  };
}): MenuOption[] => {
  // Always include Sense Calendar option
  const options = [
    createCalendarOption(
      t,
      VIRTUAL_CALENDAR_PROVIDER,
      showCount,
      connectedIdsByProvider,
    ),
  ].filter(Boolean);

  const normalizedProvider = getNormalizedServiceAccountProvider(
    serviceAccountProvider,
  );

  if (normalizedProvider) {
    const providerOption = createCalendarOption(
      t,
      normalizedProvider,
      showCount,
      connectedIdsByProvider,
    );
    if (providerOption) {
      options.push(providerOption);
    }
  }

  // @ts-expect-error - TS2322 - Type '(MenuOption | null | undefined)[]' is not assignable to type 'MenuOption[]'.
  return options;
};

export const getNormalizedServiceAccountProvider = (
  provider?: string,
): string => {
  // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type '"outlook" | "eas" | "ews" | "microsoft"'.
  if (microsoftProvidersKeys.includes(provider)) {
    return MICROSOFT_PROVIDER;
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type '"gmail" | "google"'.
  } else if (googleProvidersKeys.includes(provider)) {
    return GOOGLE_PROVIDER;
  } else if (provider === VIRTUAL_CALENDAR_PROVIDER) {
    return VIRTUAL_CALENDAR_PROVIDER;
  }
  return '';
};

export const normalizedProvider: string[] = [
  GOOGLE_PROVIDER,
  MICROSOFT_PROVIDER,
  VIRTUAL_CALENDAR_PROVIDER,
];

export const getConferencingToolOptions = (
  t: TranslationFunction,
): MenuOption[] => [
  {
    key: 'ms-teams',
    customComponent: (
      <div className={css.conferenceCustomComponent}>
        <TeamsLogo className={classify(css.logoWidth, css.logo)} />
        {t('Microsoft_Teams', 'Microsoft Teams')}
      </div>
    ),
  },
  {
    key: 'zoom',
    customComponent: (
      <div className={css.conferenceCustomComponent}>
        <ZoomLogo className={classify(css.logoWidth, css.logo)} />
        {t('Zoom', 'Zoom')}
      </div>
    ),
  },
];

export const showConnectedStatusMessage = (
  provider: string | null | undefined,
  t: TranslationFunction,
): string => {
  // @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string | undefined'.
  const normalizedProvider = getNormalizedServiceAccountProvider(provider);

  if (normalizedProvider === VIRTUAL_CALENDAR_PROVIDER) {
    return t('Connected_to_Sense_Calendar', 'Connected to Sense Calendar');
  } else if (normalizedProvider === GOOGLE_PROVIDER) {
    return t('Connected_to_Google_Calendar', 'Connected to Google Calendar');
  } else if (normalizedProvider === MICROSOFT_PROVIDER) {
    return t(
      'Connected_to_Microsoft_Calendar',
      'Connected to Microsoft Calendar',
    );
  }
  return '';
};

export type RowData = {
  data: ApiSchedulerSyncedAgent & {
    onConnect: (provider?: string) => Promise<void>;
    onDisconnect: () => Promise<void>;
    connected: boolean;
    isConnecting: boolean;
    isDisconnecting: boolean;
    isConnectionLimitReached: boolean;
    isVirtualCalendarEnabled: boolean;
    serviceAccountProvider?: string;
  };
  className?: string;
  selected?: boolean;
};

export const getTableHeaders = (t: TranslationFunction, language: string) => [
  {
    label: t('Name', 'Name'),
    key: 'name',
    sortable: true,
    render: ({data}: RowData) => {
      const {account, name} = data;
      const migrationWarning =
        account &&
        account.is_synced &&
        account.is_setup_complete &&
        account.is_upgrade_required;
      return (
        <BasicSingleCell className={css.accountName}>
          <BodyMedium className={css.agentName}>
            <TruncatedTextWithTooltip tooltip={{bodyMaxLines: 3}}>
              {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
              {name}
            </TruncatedTextWithTooltip>
            {migrationWarning && (
              <Tooltip
                body={
                  <span>
                    {`${t(
                      'Account_failed_migration',
                      'Account failed migration',
                    )}.`}
                    <br />
                    {t(
                      'Please_contact_Sense_Support',
                      'Please contact Sense Support',
                    )}
                  </span>
                }
              >
                <Icon name="warning" color="warning" />
              </Tooltip>
            )}
          </BodyMedium>
        </BasicSingleCell>
      );
    },
  },
  {
    label: t('Email', 'Email'),
    key: 'email',
    sortable: true,
    render: ({data}: RowData) => {
      return (
        <BasicSingleCell>
          <BodyMedium className={css.email}>
            <TruncatedTextWithTooltip tooltip={{bodyMaxLines: 3}}>
              {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
              {data.email}
            </TruncatedTextWithTooltip>
          </BodyMedium>
        </BasicSingleCell>
      );
    },
  },
  {
    label: t('Last_Synced', 'Last Synced'),
    key: 'email',
    render: ({data}: RowData) => {
      let lastSynced = '--';
      if (data.account) {
        lastSynced = new Date(data.account.time_updated).toLocaleString(
          language,
          {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          },
        );
      }
      return (
        <BasicSingleCell>
          <BodyMedium>{lastSynced}</BodyMedium>
        </BasicSingleCell>
      );
    },
  },
  {
    label: t('Calendar_Status', 'Calendar Status'),
    key: 'email',
    render: ({data}: RowData) => {
      const {connected, account, isVirtualCalendarEnabled} = data;
      const normalizedProvider = getNormalizedServiceAccountProvider(
        account?.provider,
      );
      const calendarConfig = getCalendarConfig(t);
      return (
        <BasicSingleCell>
          <Chip semantic={connected ? 'information' : 'danger'}>
            {connected ? (
              <>
                {isVirtualCalendarEnabled &&
                  normalizedProvider !== '' &&
                  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ "virtual-calendar": { icon: Element; label: string; }; google: { icon: Element; label: string; }; microsoft: { icon: Element; label: string; }; }'.
                  calendarConfig[normalizedProvider]?.icon}
                {t('Connected', 'Connected')}
              </>
            ) : (
              t('Not_Connected', 'Not Connected')
            )}
          </Chip>
        </BasicSingleCell>
      );
    },
  },
  {
    label: t('Video_Conferencing', 'Video Conferencing'),
    key: 'email',
    render: ({data}: RowData) => {
      const {connected} = data;
      if (!connected) {
        return <BasicSingleCell />;
      }

      const googleEnabled = data.account?.conference_integrations?.google_meet;
      const teamsEnabled =
        data.account?.conference_integrations?.microsoft_teams;
      const zoomEnabled = data.account?.conference_integrations?.zoom;

      const integrations = [
        {
          tooltipText: t('Google_Meet', 'Google Meet'),
          iconType: 'brands',
          iconName: 'google',
          enabled: googleEnabled,
        },
        {
          tooltipText: t('Microsoft_Teams', 'Microsoft Teams'),
          iconType: 'solid',
          iconName: 'envelope',
          enabled: teamsEnabled,
        },
        {
          tooltipText: t('Zoom', 'Zoom'),
          iconType: 'solid',
          iconName: 'video',
          enabled: zoomEnabled,
        },
      ];

      return (
        <BasicSingleCell>
          <div className={css.conferenceIconWrapper}>
            {integrations.map(({tooltipText, iconType, iconName, enabled}) =>
              enabled ? (
                // eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels
                <Tooltip body={tooltipText}>
                  {/* @ts-expect-error - TS2769 - No overload matches this call. */}
                  <Icon type={iconType} name={iconName} />
                </Tooltip>
              ) : null,
            )}
          </div>
        </BasicSingleCell>
      );
    },
  },
  {
    label: '',
    key: 'email',
    render: CalendarConnectionButton,
  },
];

export const createSupportTicket = () => {
  window.open('https://support.sensehq.co/hc/en-us/requests/new');
};

export const ConnectionErrorToast = () => {
  const {t} = useLabelTranslation();

  const createSupportTicket = () => {
    window.open('https://support.sensehq.co/hc/en-us/requests/new');
  };

  return (
    <Toast semantic="danger">
      <ToastTitle>{`${t('Error', 'Error')}`}</ToastTitle>
      <ToastBody>{`${t(
        'There_was_an_error_while_connecting_some_of_the_accounts_Please_try_again_in_sometime',
        'There was an error while connecting some of the accounts, Please try again in sometime.',
      )}`}</ToastBody>
      <ToastFooter>
        <Button onClick={createSupportTicket}>{`${t(
          'Create_Support_Ticket',
          'Create Support Ticket',
        )}`}</Button>
      </ToastFooter>
    </Toast>
  );
};

export const ErrorToast = () => {
  const {t} = useLabelTranslation();
  return (
    <Toast semantic="danger">
      <ToastTitle>{`${t(
        'Something_went_wrong',
        'Something went wrong',
      )}`}</ToastTitle>
    </Toast>
  );
};

export const ConnectCalendarSuccessToast = ({provider}: {provider: string}) => {
  const {t, makeKey} = useLabelTranslation();

  const providerName = providerMap[provider] || provider;
  const toastTitle = `Connection to ${providerName} Calendar successful`;
  const toastBody = `The selected users have been successfully connected to ${providerName} Calendar`;

  return (
    <Toast semantic="success">
      <ToastTitle>{`${t(makeKey(toastTitle), toastTitle)}`}</ToastTitle>
      <ToastBody>{`${t(makeKey(toastBody), toastBody)}`}</ToastBody>
    </Toast>
  );
};

export const ChangeCalendarSuccessToast = ({provider}: {provider: string}) => {
  const {t, makeKey} = useLabelTranslation();

  const providerName = providerMap[provider] || provider;
  const toastTitle = `Change to ${providerName} Calendar is successful`;
  const toastBody = `The selected users calendar have been successfully changed to ${providerName} Calendar`;

  return (
    <Toast semantic="success">
      <ToastTitle>{`${t(makeKey(toastTitle), toastTitle)}`}</ToastTitle>
      <ToastBody>{`${t(makeKey(toastBody), toastBody)}`}</ToastBody>
    </Toast>
  );
};

export const DisconnectCalendarSuccessToast = () => {
  const {t} = useLabelTranslation();
  return (
    <Toast semantic="success">
      <ToastTitle>{`${t(
        'Disconnection_of_Calendar_is_successful',
        'Disconnection of Calendar is successful',
      )}`}</ToastTitle>
      <ToastBody>{`${t(
        'The_selected_users_have_been_successfully_disconnected',
        'The selected users have been successfully disconnected',
      )}`}</ToastBody>
    </Toast>
  );
};

export const RenderEmptyState = () => {
  const {t} = useLabelTranslation();
  return (
    <EmptyState
      title={t('No_result_found', 'No result found')}
      description={t(
        'We_couldn\'t_find_any_matching_results_Try_again_with_different_filters',
        'We couldn\'t find any matching results. Try again with different filters',
      )}
      imageVariant="data"
    />
  );
};

export const ConnectionLimitErrorToast = ({
  numberOfConnectionsLeft,
}: {
  numberOfConnectionsLeft: number;
}) => {
  const {t} = useLabelTranslation();
  const currentDate = new Date();
  return (
    <Toast
      semantic="information"
      time={`${t('Today', 'Today')} ${displayTime(currentDate)}`}
    >
      <ToastTitle>{`${t(
        'Connection_Limit_Reached',
        'Connection Limit Reached',
      )}`}</ToastTitle>
      <ToastBody>{`${t(
        'You_can_only_connect_numberOfConnectionsLeft_more_users_To_add_additional_users_please_disconnect_some_existing_connections',
        {numberOfConnectionsLeft},
      )}`}</ToastBody>
    </Toast>
  );
};
